/*
 * Mystery-pack selection logic (pure functions, no DOM).
 * Used by the product page (window.MysteryCore) and by the Node test suite (module.exports).
 *
 * Every pack is a normal Shopify product whose options are sizes, e.g.
 *   Men's / Women's pack:  one option   "Size"
 *   Couples pack:          two options  "Men's size", "Women's size"
 * Each combination is a real Shopify variant with its own price and availability.
 * Prices shown on the page are for display only; Shopify charges the variant price at checkout.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.MysteryCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function norm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  /** raw = { options: [{name, values}], variants: [{id, options, price, available}] } */
  function createModel(raw) {
    var options = (raw.options || []).map(function (o) {
      return { name: typeof o === 'string' ? o : o.name, values: (o && o.values) || [] };
    });
    return {
      options: options,
      variants: (raw.variants || []).map(function (v) {
        return { id: v.id, options: v.options || [], price: Number(v.price), available: !!v.available };
      })
    };
  }

  /** selected = array of chosen values (or null) per option position */
  function findVariant(model, selected) {
    if (!selected || selected.length !== model.options.length) return null;
    for (var i = 0; i < selected.length; i++) if (!selected[i]) return null;
    for (var j = 0; j < model.variants.length; j++) {
      var v = model.variants[j];
      var match = true;
      for (var k = 0; k < selected.length; k++) {
        if (norm(v.options[k]) !== norm(selected[k])) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  /**
   * For every option, which values can still be bought given the OTHER options already chosen?
   * Returns [{ value: boolean }, ...] in option order.
   */
  function optionAvailability(model, selected) {
    selected = selected || [];
    return model.options.map(function (opt, i) {
      var out = {};
      opt.values.forEach(function (value) {
        out[value] = model.variants.some(function (v) {
          if (!v.available) return false;
          if (norm(v.options[i]) !== norm(value)) return false;
          for (var k = 0; k < model.options.length; k++) {
            if (k === i || !selected[k]) continue;
            if (norm(v.options[k]) !== norm(selected[k])) return false;
          }
          return true;
        });
      });
      return out;
    });
  }

  /** Lowest price across purchasable variants (or all variants if none purchasable). */
  function fromPrice(model) {
    var pool = model.variants.filter(function (v) { return v.available; });
    if (!pool.length) pool = model.variants;
    if (!pool.length) return null;
    return Math.min.apply(null, pool.map(function (v) { return v.price; }));
  }

  /** { total, exact, variant } — exact=false means "from" price until every option is chosen. */
  function priceSummary(model, selected) {
    var variant = findVariant(model, selected);
    if (variant) return { total: variant.price, exact: true, variant: variant };
    return { total: fromPrice(model), exact: false, variant: null };
  }

  /** Validate the current choice. Returns { ok, reason, missing, variant }. */
  function validate(model, selected, quantity) {
    selected = selected || [];
    for (var i = 0; i < model.options.length; i++) {
      var val = selected[i];
      if (!val) return { ok: false, reason: 'missing', missing: i };
      var known = model.options[i].values.map(norm).indexOf(norm(val)) > -1;
      if (!known) return { ok: false, reason: 'invalid', missing: i };
    }
    var variant = findVariant(model, selected);
    if (!variant) return { ok: false, reason: 'unavailable' };
    if (!variant.available) return { ok: false, reason: 'soldout', variant: variant };
    var qty = Number(quantity == null ? 1 : quantity);
    if (!(qty >= 1 && qty <= 10 && Math.floor(qty) === qty)) return { ok: false, reason: 'quantity', variant: variant };
    return { ok: true, variant: variant };
  }

  /** Format cents with a Shopify money format string, e.g. "R {{amount}}". */
  function formatMoney(cents, format) {
    if (cents == null || isNaN(cents)) return '';
    format = format || 'R {{amount}}';
    function fmt(number, precision, thousands, decimal) {
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount': return fmt(cents, 2, ',', '.');
        case 'amount_no_decimals': return fmt(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return fmt(cents, 0, ' ', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2, ',', '.');
      }
    });
  }

  return {
    createModel: createModel,
    findVariant: findVariant,
    optionAvailability: optionAvailability,
    fromPrice: fromPrice,
    priceSummary: priceSummary,
    validate: validate,
    formatMoney: formatMoney
  };
});
